import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AccessJwt,
  LoginRequest,
  RefreshSessionRequest,
  RoleCode,
  SignupDataDto,
} from '../models/api/auth.dto';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly base = `${environment.apiUrl}/auth`;

  private readonly AUTH_TOKEN_KEY = 'pc_auth_token';
  private readonly REFRESH_TOKEN_KEY = 'pc_refresh_token';
  private readonly USER_ID_KEY = 'pc_user_id';
  private readonly STRIPE_CUSTOMER_ID_KEY = 'pc_stripe_customer_id';
  private readonly ROLES_KEY = 'pc_roles';
  private readonly EMAIL_VERIFIED_KEY = 'pc_email_verified';

  // ── Login ──────────────────────────────────────────────────────────────────

  login(credentials: LoginRequest): Observable<AccessJwt> {
    // Spring Security expects a device ID header — we generate a stable one per browser
    const deviceId = this.getOrCreateDeviceId();
    return this.http
      .post<AccessJwt>(`${this.base}/login`, credentials, {
        headers: { 'X-Device-Id': deviceId },
      })
      .pipe(
        tap((jwt) => this.storeSession(jwt)),
        catchError((err) => throwError(() => err)),
      );
  }

  // ── Signup ─────────────────────────────────────────────────────────────────

  // Session is stored even when emailVerified is false — the app does the same
  register(data: SignupDataDto): Observable<AccessJwt> {
    const deviceId = this.getOrCreateDeviceId();
    return this.http
      .post<AccessJwt>(`${environment.apiUrl}/bff/signup`, data, {
        headers: { 'X-Device-Id': deviceId },
      })
      .pipe(tap((jwt) => this.storeSession(jwt)));
  }

  resendVerifyEmail(): Observable<void> {
    return this.http.post<void>(`${this.base}/resend-verify-email`, {});
  }

  sendResetPasswordEmail(email: string): Observable<void> {
    return this.http.post<void>(
      `${this.base}/send-reset-pass-email/${encodeURIComponent(email)}`,
      {},
    );
  }

  // ── Refresh ────────────────────────────────────────────────────────────────

  refresh(): Observable<AccessJwt> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    if (!token || !refreshToken) return throwError(() => new Error('No session'));

    const deviceId = this.getOrCreateDeviceId();
    const body: RefreshSessionRequest = { token, refreshToken };

    return this.http
      .post<AccessJwt>(`${this.base}/refresh`, body, {
        headers: { 'X-Device-Id': deviceId },
      })
      .pipe(
        tap((jwt) => this.storeSession(jwt)),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        }),
      );
  }

  // ── Logout ─────────────────────────────────────────────────────────────────

  logout(redirectTo = '/login'): void {
    this.clearSession();
    this.router.navigate([redirectTo]);
  }

  // ── Session helpers ────────────────────────────────────────────────────────

  isLoggedIn(): boolean {
    if (!this.getToken()) return false;
    // A session stored before the verified flag existed can't be routed safely: force a new login
    if (localStorage.getItem(this.EMAIL_VERIFIED_KEY) === null) {
      this.clearSession();
      return false;
    }
    return true;
  }

  isEmailVerified(): boolean {
    return localStorage.getItem(this.EMAIL_VERIFIED_KEY) === 'true';
  }

  getToken(): string | null {
    return localStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getUserId(): number | null {
    const v = localStorage.getItem(this.USER_ID_KEY);
    return v ? Number(v) : null;
  }

  getStripeCustomerId(): string | null {
    return localStorage.getItem(this.STRIPE_CUSTOMER_ID_KEY);
  }

  getRoles(): RoleCode[] {
    try {
      return JSON.parse(localStorage.getItem(this.ROLES_KEY) ?? '[]') as RoleCode[];
    } catch {
      return [];
    }
  }

  hasRole(role: RoleCode): boolean {
    return this.getRoles().includes(role);
  }

  isAthlete(): boolean {
    return this.hasRole('ROLE_ATHLETE');
  }

  isBoxOwner(): boolean {
    return this.hasRole('ROLE_BOX_OWNER');
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private storeSession(jwt: AccessJwt): void {
    localStorage.setItem(this.AUTH_TOKEN_KEY, jwt.authToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, jwt.refreshToken);
    localStorage.setItem(this.USER_ID_KEY, String(jwt.userId));
    localStorage.setItem(this.STRIPE_CUSTOMER_ID_KEY, jwt.stripeCustomerId);
    localStorage.setItem(this.ROLES_KEY, JSON.stringify(jwt.roles));
    localStorage.setItem(this.EMAIL_VERIFIED_KEY, String(jwt.emailVerified));
  }

  private clearSession(): void {
    [
      this.AUTH_TOKEN_KEY,
      this.REFRESH_TOKEN_KEY,
      this.USER_ID_KEY,
      this.STRIPE_CUSTOMER_ID_KEY,
      this.ROLES_KEY,
      this.EMAIL_VERIFIED_KEY,
    ].forEach((k) => localStorage.removeItem(k));
  }

  private getOrCreateDeviceId(): string {
    const key = 'pc_device_id';
    let id = localStorage.getItem(key);
    if (!id) {
      // Generate a UUID v4-like string
      id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
      localStorage.setItem(key, id);
    }
    return id;
  }
}
